import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { useSearchParams } from 'react-router-dom';
import { loginRequest } from './authConfig';


// Use relative paths for API calls.
const api = axios.create({
    baseURL: ''
});


const launchpadGlassCardStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(64px) saturate(200%)',
    WebkitBackdropFilter: 'blur(64px) saturate(200%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(255, 255, 255, 0.04), 0 30px 86px rgba(0, 0, 0, 0.44)',
};

const operationsGlassCardStyles = `
    .operations-launchpad-card:hover,
    .operations-launchpad-card:focus-visible {
        border-color: rgba(255, 255, 255, 0.18) !important;
        background-color: rgba(255, 255, 255, 0.045) !important;
    }
`;


// --- HELPER: Random Avatar Gradient ---
const getAvatarGradient = (name) => {
    const gradients = [
        'from-red-500 to-orange-600',
        'from-blue-500 to-cyan-600',
        'from-emerald-500 to-green-600',
        'from-violet-500 to-purple-600',
        'from-amber-500 to-yellow-600',
        'from-pink-500 to-rose-600',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
};

const getSafeName = (user) => {
    // If user.name is valid, use it.
    if (user.name && user.name.trim().length > 0) return user.name;
    // Fallback: check columns.
    return user.columns["Name"] || user.columns["Person"] || user.columns["Full Name"] || 'Unknown User';
};

const getInitials = (name) => {
    return (name || '')
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const formatDisplayDate = (dateString) => {
    if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return null;
    try {
        const date = new Date(dateString + 'T00:00:00');
        return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
    } catch (e) { return dateString; }
};



// --- COMPONENT: Stats Summary HUD ---
// --- COMPONENT: StatCard (Dashboard Style) ---
const StatCard = ({ label, value, subtext, trend }) => {
    return (
        <div className="operations-launchpad-card relative group overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1" style={launchpadGlassCardStyle}>
            <div className="relative z-10 flex flex-col h-full justify-between items-center text-center">
                <div className="flex justify-center items-center w-full mb-2 relative">
                    <span className="text-xs font-semibold uppercase" style={{ color: 'rgba(255, 255, 255, 0.5)', letterSpacing: '0.1em' }}>{label}</span>
                    {trend && (
                        <span className={`absolute right-0 top-0 text-[10px] font-mono px-1.5 py-0.5 rounded border ${trend === 'up' ? 'border-green-500/30 bg-green-500/10 text-green-400' : 'border-neutral-500/30 bg-neutral-500/10 text-neutral-400'}`}>
                            {trend === 'up' ? '▲' : '▼'}
                        </span>
                    )}
                </div>

                <div className="flex items-baseline justify-center gap-2 mt-1">
                    <span className="text-3xl font-semibold text-[#FFFFFF] tracking-tighter">
                        {value}
                    </span>
                    {subtext && <span className="text-xs text-gray-500 font-medium">{subtext}</span>}
                </div>
            </div>
        </div>
    );
};

const StatsSummary = ({ users, groups }) => {
    const totalUsers = users.length;
    const activeUsers = users.filter(u => (u.columns["Status"] || '').toLowerCase() === 'active').length;
    const deactivatedUsers = users.filter(u => (u.columns["Status"] || '').toLowerCase().includes('deactivated')).length;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-2">
            <StatCard label="Total Accounts" value={totalUsers} />
            <StatCard label="Active Users" value={activeUsers} />
            <StatCard label="Deactivated" value={deactivatedUsers} />
        </div>
    );
};


// --- COMPONENT: Detail Item (View Mode) ---
const DetailItem = ({ title, value }) => {
    let displayValue = value;
    if (Array.isArray(value)) displayValue = value.length > 0 ? value.join(', ') : null;

    return (
        <div className="py-4 border-b border-white/5 last:border-0 group">
            <dt className="text-xs font-mono text-gray-400 uppercase tracking-widest mb-1 group-hover:text-white/70 transition-colors">{title}</dt>
            <dd className="text-sm text-gray-200 font-medium">{displayValue || <span className="text-gray-600 italic">Not set</span>}</dd>
        </div>
    );
};

// --- FORM COMPONENTS (Glass Theme) ---
const FormInput = ({ label, id, ...props }) => (
    <div className="group">
        {label && <label htmlFor={id} className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 group-focus-within:text-white transition-colors">{label}</label>}
        <input
            id={id}
            className={`block w-full bg-[#050505]/50 border border-white/10 text-white rounded-xl py-2.5 px-4 
            focus:ring-1 focus:ring-white/30 focus:border-white/30 focus:outline-none focus:bg-white/5 transition-all duration-200
            placeholder-gray-600 text-sm`}
            {...props}
        />
    </div>
);

const FormSelect = ({ label, id, children, ...props }) => (
    <div className="group">
        <label htmlFor={id} className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 group-focus-within:text-white transition-colors">{label}</label>
        <div className="relative">
            <select
                id={id}
                className="block w-full appearance-none bg-[#050505]/50 border border-white/10 text-white rounded-xl py-2.5 px-4 pr-10 
                focus:ring-1 focus:ring-white/30 focus:border-white/30 focus:outline-none focus:bg-white/5 transition-all duration-200 text-sm"
                {...props}
            >
                {children}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
        </div>
    </div>
);

const FormCheckboxGroup = ({ label, options, selected, onChange, name }) => {
    const handleCheckChange = (optionName) => {
        const currentSelected = Array.isArray(selected) ? [...selected] : [];
        const index = currentSelected.indexOf(optionName);
        if (index > -1) currentSelected.splice(index, 1);
        else currentSelected.push(optionName);
        onChange({ target: { name, value: currentSelected } });
    };

    return (
        <div>
            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">{label}</label>
            <div className="bg-[#050505]/30 border border-white/5 rounded-xl p-1 max-h-48 overflow-y-auto custom-scrollbar">
                {options.length > 0 ? options.map(option => (
                    <button
                        type="button"
                        key={option}
                        onClick={() => handleCheckChange(option)}
                        className="w-full flex items-center space-x-3 cursor-pointer p-2.5 rounded-lg hover:bg-white/5 transition-colors group text-left"
                    >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all duration-200 ${Array.isArray(selected) && selected.includes(option) ? 'bg-red-600 border-red-600' : 'border-gray-600 group-hover:border-gray-400'}`}>
                            {Array.isArray(selected) && selected.includes(option) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <span className={`text-sm transition-colors ${Array.isArray(selected) && selected.includes(option) ? 'text-white font-medium' : 'text-gray-400 group-hover:text-gray-300'}`}>{option}</span>
                    </button>
                )) : (
                    <div className="p-3 text-center text-xs text-gray-500 italic">No options available</div>
                )}
            </div>
        </div>
    );
};

// --- COMPONENT: User Edit Panel (Slide-Over) ---
const UserEditPanel = ({ user, onClose, columns, onSave, onTerminate, isTerminated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({ ...user.columns });
    const [isVisible, setIsVisible] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        setFormData({
            ...user.columns,
            "SharedMailboxes": Array.isArray(user.columns["SharedMailboxes"]) ? user.columns["SharedMailboxes"] : [],
            "SharePoint Sites": Array.isArray(user.columns["SharePoint Sites"]) ? user.columns["SharePoint Sites"] : [],
        });
        setIsEditing(false);
        setIsVisible(true);
    }, [user]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            await onSave(user.id, user.columns, formData);
            handleClose();
        } catch (error) { setIsSubmitting(false); }
    };

    const getOptions = (title) => columns.find(c => c.title === title)?.options || [];

    return (
        <div className={`fixed inset-0 z-50 flex justify-end transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>

            {/* Panel */}
            <div
                className={`relative w-full max-w-md bg-[#0a0a0a]/80 backdrop-blur-2xl border-l border-white/10 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
                ref={panelRef}
            >
                {/* Header */}
                <div className="shrink-0 h-20 flex items-center justify-between px-6 border-b border-white/5 bg-white/[0.02]">
                    <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarGradient(user.name)} flex items-center justify-center text-white font-bold shadow-lg`}>
                            {getInitials(user.name)}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white leading-tight">{user.name}</h2>
                            <p className="text-xs text-gray-400 font-mono">{user.columns["Email Address"] || 'No email'}</p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-2 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                    {!isEditing ? (
                        <div className="space-y-1">
                            <div className="flex items-center justify-between mb-6">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${(user.columns["Status"] || '').toLowerCase() === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                    (user.columns["Status"] || '').toLowerCase().includes('deactivated') ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                                        'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                                    }`}>
                                    {user.columns["Status"] || 'Unknown Status'}
                                </span>
                                <div className="flex gap-2">
                                    {!isTerminated && (
                                        <button onClick={() => onTerminate(user)} className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 rounded-lg transition-colors">Terminate</button>
                                    )}
                                    <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 text-xs font-medium text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors">Edit Profile</button>
                                </div>
                            </div>

                            <DetailItem title="Title" value={user.columns["Title"]} />
                            <DetailItem title="Location" value={user.columns["Location"]} />
                            <DetailItem title="Phone Phone" value={user.columns["Phone #"]} />
                            <DetailItem title="Start Date" value={formatDisplayDate(user.columns["Start Date"])} />
                            <DetailItem title="Separation Date" value={formatDisplayDate(user.columns["Separation Date"])} />
                            <DetailItem title="O365 License" value={user.columns["O365 License"]} />
                            <DetailItem title="Shared Mailboxes" value={user.columns["SharedMailboxes"]} />
                            <DetailItem title="SharePoint Sites" value={user.columns["SharePoint Sites"]} />
                            <DetailItem title="Onboard Complete" value={user.columns["Onboard Complete"]} />
                            <DetailItem title="Progress/Status" value={user.columns["Progress/Status"]} />
                            <DetailItem title="Temporary Password" value={user.columns["Temporary Password"]} />
                        </div>
                    ) : (
                        <div className="space-y-5 animate-fadeIn">
                            <FormInput label="Email Address" id="Email Address" name="Email Address" type="email" value={formData["Email Address"] || ''} onChange={handleChange} />
                            <FormSelect label="Title" id="Title" name="Title" value={formData["Title"] || ''} onChange={handleChange}>
                                <option value="">Select title...</option>
                                {getOptions("Title").map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </FormSelect>
                            <FormSelect label="Location" id="Location" name="Location" value={formData["Location"] || ''} onChange={handleChange}>
                                <option value="">Select location...</option>
                                {getOptions("Location").map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </FormSelect>
                            <FormSelect label="Status" id="Status" name="Status" value={formData["Status"] || ''} onChange={handleChange}>
                                <option value="">Select status...</option>
                                {getOptions("Status").map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </FormSelect>

                            <div className="grid grid-cols-2 gap-4">
                                <FormInput label="Phone #" id="Phone #" name="Phone #" type="tel" value={formData["Phone #"] || ''} onChange={handleChange} />
                                <FormInput label="Start Date" id="Start Date" name="Start Date" type="date" value={formData["Start Date"] || ''} onChange={handleChange} />
                            </div>

                            <FormSelect label="O365 License" id="O365 License" name="O365 License" value={formData["O365 License"] || ''} onChange={handleChange}>
                                <option value="">Select license...</option>
                                {getOptions("O365 License").map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </FormSelect>

                            <FormCheckboxGroup label="Shared Mailboxes" name="SharedMailboxes" options={getOptions("SharedMailboxes")} selected={formData["SharedMailboxes"]} onChange={handleChange} />
                            <FormCheckboxGroup label="SharePoint Sites" name="SharePoint Sites" options={getOptions("SharePoint Sites")} selected={formData["SharePoint Sites"]} onChange={handleChange} />

                            <FormInput label="Temporary Password" id="Temporary Password" name="Temporary Password" value={formData["Temporary Password"] || ''} onChange={handleChange} />
                            <FormInput label="Separation Date" id="Separation Date" name="Separation Date" type="date" value={formData["Separation Date"] || ''} onChange={handleChange} />
                        </div>
                    )}
                </div>

                {/* Footer */}
                {isEditing && (
                    <div className="shrink-0 p-6 border-t border-white/5 bg-white/[0.02] flex justify-end gap-3">
                        <button type="button" onClick={() => setIsEditing(false)} disabled={isSubmitting} className="px-5 py-2.5 text-sm font-medium text-gray-300 hover:text-white transition-colors">Cancel</button>
                        <button type="button" onClick={handleSave} disabled={isSubmitting} className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 rounded-lg hover:shadow-lg hover:shadow-red-900/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed">
                            {isSubmitting ? 'Saving Changes...' : 'Save Updates'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- COMPONENT: Add User Modal (Standardized) ---
const AddUserModal = ({ onClose, onSubmit, groups, columns }) => {
    const [formData, setFormData] = useState({ name: '', groupId: groups[0]?.id || '', email: '', title: '', location: '', status: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => setIsVisible(true), []);
    const handleClose = () => { setIsVisible(false); setTimeout(onClose, 300); };
    const getOptions = (title) => columns.find(c => c.title.toLowerCase() === title.toLowerCase())?.options || [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try { await onSubmit(formData); handleClose(); }
        catch (e) { setIsSubmitting(false); }
    };

    return (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={handleClose}></div>
            <form onSubmit={handleSubmit} className={`relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] transition-all duration-300 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
                <div className="px-6 py-5 border-b border-white/5 bg-white/[0.02] flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white">Add New User</h3>
                    <button type="button" onClick={handleClose} className="text-gray-500 hover:text-white transition-colors"><svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
                    <FormInput label="Full Name" id="name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required autoFocus />
                    <FormSelect label="Department / Group" id="groupId" value={formData.groupId} onChange={e => setFormData({ ...formData, groupId: e.target.value })} required>
                        {groups.filter(g => g.title.toLowerCase() !== 'inactive/terminated').map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                    </FormSelect>
                    <FormInput label="Email Address" id="email" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                    <FormSelect label="Job Title" id="title" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}>
                        <option value="">Select title...</option>
                        {getOptions("title").map(o => <option key={o} value={o}>{o}</option>)}
                    </FormSelect>
                    <FormSelect label="Location" id="location" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}>
                        <option value="">Select location...</option>
                        {getOptions("location").map(o => <option key={o} value={o}>{o}</option>)}
                    </FormSelect>
                    <FormSelect label="Status" id="status" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                        <option value="">Select status...</option>
                        {getOptions("status").map(o => <option key={o} value={o}>{o}</option>)}
                    </FormSelect>
                </div>
                <div className="px-6 py-4 border-t border-white/5 bg-white/[0.02] flex justify-end gap-3">
                    <button type="button" onClick={handleClose} className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-6 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-500 transition-colors shadow-lg shadow-red-900/20 disabled:opacity-50">Create Account</button>
                </div>
            </form>
        </div>
    );
};

// --- COMPONENT: Toast ---
const ToastNotification = ({ message, type, onHide }) => {
    useEffect(() => { const t = setTimeout(onHide, 3000); return () => clearTimeout(t); }, [onHide]);
    return (
        <div className={`fixed bottom-8 right-8 z-[100] flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl backdrop-blur-xl border animate-fadeInUp ${type === 'error' ? 'bg-red-900/80 border-red-500/30 text-white' : 'bg-[#1a1a1a]/90 border-white/10 text-white'}`}>
            <div className={`w-2 h-2 rounded-full ${type === 'error' ? 'bg-red-400' : 'bg-emerald-400'}`}></div>
            <p className="text-sm font-medium">{message}</p>
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
export default function AdminPage({ isPreviewMode = false }) {
    const { instance, inProgress } = useMsal();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [columns, setColumns] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const openedLocationAccountRef = useRef('');
    const locationAccountRowRefs = useRef({});
    const scrolledLocationAccountRef = useRef('');
    const [focusedLocationAccountId, setFocusedLocationAccountId] = useState('');
    const locationAccountId = searchParams.get('locationAccountId');

    const scrollLocationAccountRowIntoView = useCallback((row, accountId) => {
        if (!row) return;
        if (scrolledLocationAccountRef.current === accountId) return;
        scrolledLocationAccountRef.current = accountId;
        setTimeout(() => row.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth'
        }), 0);
    }, []);

    // --- Data Fetching Logic (Memoized) ---
    const getAuthToken = useCallback(async () => {
        const accounts = instance.getAllAccounts();
        if (!accounts[0]) throw new Error("No accounts");
        const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        return res.accessToken;
    }, [instance]);

    const fetchData = useCallback(async () => {
        if (inProgress !== "none") return;
        setLoading(true); setError('');
        try {
            const token = await getAuthToken();
            const res = await api.get('/api/admin/users', { headers: { 'x-auth-token': token } });
            setUsers(res.data.users);
            setGroups(res.data.groups);
            setColumns(res.data.columns);
        } catch (err) {
            const isAuthorizationError = err.response && (err.response.status === 401 || err.response.status === 403);
            if (isPreviewMode && isAuthorizationError) {
                setUsers([]);
                setGroups([]);
                setColumns([]);
            } else {
                console.error(err);
                setError(err.response?.status === 403 ? 'Access Denied' : 'Failed to load user database.');
            }
        } finally { setLoading(false); }
    }, [getAuthToken, inProgress, isPreviewMode]);

    useEffect(() => {
        if (isPreviewMode) {
            setLoading(false);
            setError('');
            return;
        }

        fetchData();
    }, [fetchData, isPreviewMode]);

    useEffect(() => {
        if (!locationAccountId || loading || users.length === 0) return;
        if (openedLocationAccountRef.current === locationAccountId) return;

        openedLocationAccountRef.current = locationAccountId;
        const matchedUser = users.find(user => String(user.id) === String(locationAccountId));
        if (matchedUser) {
            setSearchQuery('');
            scrolledLocationAccountRef.current = '';
            setFocusedLocationAccountId(String(matchedUser.id));
            setSelectedUser(matchedUser);
            return;
        }

        setToast({
            show: true,
            message: 'Location account not found in Operations',
            type: 'error'
        });
    }, [loading, locationAccountId, users]);

    useEffect(() => {
        if (!focusedLocationAccountId) return undefined;
        const row = locationAccountRowRefs.current[focusedLocationAccountId];
        if (!row) return undefined;

        scrollLocationAccountRowIntoView(row, focusedLocationAccountId);
        return undefined;
    }, [focusedLocationAccountId, scrollLocationAccountRowIntoView]);

    // --- Handlers ---
    const showToast = (msg, type = 'success') => setToast({ show: true, message: msg, type });

    // Helper to map UI form data back to Monday.com column IDs and types
    const buildMondayPayload = (formData) => {
        const payload = {};
        Object.entries(formData).forEach(([key, value]) => {
            const col = columns.find(c => c.title === key);
            if (!col) return;

            // If the field is totally emptied out on the UI, we must send an explicit null 
            // to Monday.com to tell it to clear the cell, regardless of the column type.
            if (value === null || value === undefined || value === '') {
                payload[col.id] = null;
                return;
            }

            let formattedValue;
            if (col.type === 'status') {
                formattedValue = { label: String(value) };
            } else if (col.type === 'dropdown') {
                formattedValue = Array.isArray(value) ? { labels: value } : { label: String(value) };
            } else if (col.type === 'date') {
                formattedValue = { date: String(value) };
            } else if (col.type === 'phone') {
                // Keep only digits and '+' for the phone number
                const cleanPhone = String(value).replace(/[^0-9+]/g, '');
                formattedValue = { phone: cleanPhone, countryShortName: "US" };
            } else if (col.type === 'email') {
                formattedValue = { email: String(value), text: String(value) };
            } else if (col.type === 'text') {
                formattedValue = String(value);
            } else if (Array.isArray(value)) {
                formattedValue = value.join(', ');
            } else {
                formattedValue = String(value);
            }

            payload[col.id] = formattedValue;
        });
        return payload;
    };

    const handleCreateUser = async (formData) => {
        try {
            const token = await getAuthToken();

            const uiData = {
                "Email Address": formData.email,
                "Title": formData.title,
                "Location": formData.location,
                "Status": formData.status
            };

            const body = {
                name: formData.name,
                groupId: formData.groupId,
                columnValues: buildMondayPayload(uiData)
            };

            await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/users/create`,
                body,
                { headers: { 'x-auth-token': token } }
            );

            showToast('User created successfully');
            fetchData();
        } catch (e) {
            showToast('Failed to create user', 'error');
            console.error('Create User API Error:', e);
        }
    };

    const handleUpdateUser = async (userId, originalCols, editData) => {
        // --- NEW: Filter out unmodified fields to keep Audit Logs clean ---
        const changedData = {};
        Object.keys(editData).forEach(key => {
            const originalVal = originalCols[key];
            const newVal = editData[key];

            // Deep compare arrays (for dropdowns/tags)
            if (Array.isArray(originalVal) || Array.isArray(newVal)) {
                if (JSON.stringify(originalVal || []) !== JSON.stringify(newVal || [])) {
                    changedData[key] = newVal;
                }
            } else if (originalVal !== newVal) {
                // To avoid "clearing" empty fields that were already empty
                if (!(!originalVal && !newVal)) {
                    changedData[key] = newVal;
                }
            }
        });

        if (Object.keys(changedData).length === 0) {
            showToast('No changes detected');
            return;
        }
        // ------------------------------------------------------------------

        // Optimistic UI Update
        const oldUsers = [...users];
        setUsers(users.map(u => u.id === userId ? { ...u, columns: { ...u.columns, ...changedData } } : u));
        try {
            const token = await getAuthToken();
            const payload = buildMondayPayload(changedData);

            await axios.put(
                `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/users/${userId}/update`,
                { columnValues: payload },
                { headers: { 'x-auth-token': token } }
            );
            showToast('Profile updated');
        } catch (e) {
            setUsers(oldUsers);
            showToast('Update failed. Reverting changes.', 'error');
            console.error('Update User Frontend Error:', e);
        }
    };

    // --- Filtering ---
    const usersByGroup = useMemo(() => {
        if (!searchQuery) return users.reduce((acc, u) => ({ ...acc, [u.groupId]: [...(acc[u.groupId] || []), u] }), {});
        const lowerQ = searchQuery.toLowerCase();
        return users.filter(u =>
            u.name.toLowerCase().includes(lowerQ) ||
            (u.columns["Email Address"] || '').toLowerCase().includes(lowerQ) ||
            (u.columns["Title"] || '').toLowerCase().includes(lowerQ)
        ).reduce((acc, u) => ({ ...acc, [u.groupId]: [...(acc[u.groupId] || []), u] }), {});
    }, [users, searchQuery]);

    if (loading) return <div className="h-full flex items-center justify-center text-sm text-gray-500">Loading user database...</div>;
    if (error) return <div className="h-full flex items-center justify-center text-red-400">{error}</div>;

    return (


        <div className="flex flex-col h-full animate-fadeInUp px-4 md:px-6 py-4 md:py-6 overflow-hidden relative z-10">
            <style>{operationsGlassCardStyles}</style>
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 shrink-0 gap-4">

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
                    <div className="relative group w-full sm:w-auto">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-500 group-focus-within:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full text-white rounded-xl pl-10 pr-4 py-2 sm:w-64 focus:sm:w-80 transition-all duration-300 focus:outline-none placeholder-gray-500 text-sm"
                            style={launchpadGlassCardStyle}
                            onFocus={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.045)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.03)';
                            }}
                        />
                    </div>
                    <button
                        onClick={() => setIsAddUserModalOpen(true)}
                        className="operations-launchpad-card flex items-center justify-center gap-2 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap hover:-translate-y-0.5"
                        style={launchpadGlassCardStyle}
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Add New User
                    </button>
                </div>
            </div>

            {/* Stats */}
            <StatsSummary users={users} groups={groups} />

            {/* Groups Grid */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-8 custom-scrollbar pb-20">
                {groups.map(group => {
                    const groupUsers = usersByGroup[group.id] || [];
                    if (!groupUsers.length && searchQuery) return null;

                    return (
                        <div key={group.id} className="animate-fadeIn">
                            <div className="flex items-center gap-3 mb-6 mt-8">
                                <h2 className="text-3xl font-bold text-white tracking-tighter uppercase font-display">{group.title}</h2>
                                <span className="bg-white/10 text-gray-300 text-xs px-2 py-0.5 rounded-md font-mono border border-white/5">{groupUsers.length}</span>
                                <div className="h-[1px] flex-1 bg-gradient-to-r from-white/10 to-transparent ml-2"></div>
                            </div>

                            {groupUsers.length === 0 ? (
                                <div className="glass-panel p-8 rounded-2xl border border-white/5 bg-[#0a0a0a]/20 text-center text-gray-500 text-sm italic">
                                    No users found in this group.
                                </div>
                            ) : (
                                <div className="glass-panel rounded-2xl overflow-x-auto border border-white/5 bg-[#0a0a0a]/40 shadow-2xl backdrop-blur-md custom-scrollbar">
                                    <table className="w-full text-left border-collapse min-w-[800px]">
                                        <thead>
                                            <tr className="border-b border-white/10 bg-white/5">
                                                <th className="px-6 py-4 text-xs font-mono font-medium text-gray-500 uppercase tracking-widest" style={{ width: '20%' }}>Name</th>
                                                <th className="px-6 py-4 text-xs font-mono font-medium text-gray-500 uppercase tracking-widest" style={{ width: '25%' }}>Email</th>
                                                <th className="px-6 py-4 text-xs font-mono font-medium text-gray-500 uppercase tracking-widest" style={{ width: '15%' }}>Role</th>
                                                <th className="px-6 py-4 text-xs font-mono font-medium text-gray-500 uppercase tracking-widest" style={{ width: '15%' }}>Location</th>
                                                <th className="px-6 py-4 text-xs font-mono font-medium text-gray-500 uppercase tracking-widest" style={{ width: '15%' }}>Status</th>
                                                <th className="px-6 py-4 text-xs font-mono font-medium text-gray-500 uppercase tracking-widest text-right" style={{ width: '10%' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            {groupUsers.map((user, idx) => {
                                                const isActive = (user.columns["Status"] || '').toLowerCase() === 'active';
                                                const displayName = getSafeName(user);
                                                const userId = String(user.id);
                                                const isFocusedLocationAccount = userId === focusedLocationAccountId;

                                                // Data Patch: If Email Address column contains something that doesn't look like an email, 
                                                // but looks like a name, ignore it.
                                                let displayEmail = user.columns["Email Address"] || user.columns["Email"] || '-';
                                                if (displayEmail !== '-' && !displayEmail.includes('@') && displayEmail.includes(' ')) {
                                                    // Suspicious: Email column has no @ but has a space? It's probably a name.
                                                    displayEmail = '-';
                                                }

                                                return (
                                                    <tr
                                                        key={user.id}
                                                        ref={(node) => {
                                                            if (node) {
                                                                locationAccountRowRefs.current[userId] = node;
                                                                if (isFocusedLocationAccount) scrollLocationAccountRowIntoView(node, userId);
                                                            } else {
                                                                delete locationAccountRowRefs.current[userId];
                                                            }
                                                        }}
                                                        data-testid={`operations-account-row-${user.id}`}
                                                        data-focused={isFocusedLocationAccount ? 'true' : undefined}
                                                        onClick={() => setSelectedUser(user)}
                                                        className={`group hover:bg-white/[0.03] hover:bg-gradient-to-r hover:from-transparent hover:via-blue-500/5 hover:to-transparent transition-all duration-150 relative cursor-pointer ${isFocusedLocationAccount ? 'bg-white/[0.06] ring-1 ring-white/10' : ''}`}
                                                        style={{ animationDelay: `${idx * 50}ms` }}
                                                    >
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(displayName)} flex items-center justify-center text-[10px] text-white font-bold shadow-md`}>
                                                                    {getInitials(displayName)}
                                                                </div>
                                                                <span className="text-sm font-medium text-white group-hover:text-blue-100 transition-colors">{displayName}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-400 font-light">{displayEmail}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-300">
                                                            {Array.isArray(user.columns["Title"]) ? user.columns["Title"][0] : user.columns["Title"]}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-400">
                                                            {Array.isArray(user.columns["Location"]) ? user.columns["Location"][0] : user.columns["Location"]}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${isActive ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                                                                {user.columns["Status"] || 'Unknown'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <svg className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors ml-auto transform group-hover:translate-x-1 duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Overlays */}
            {selectedUser && <UserEditPanel user={selectedUser} onClose={() => setSelectedUser(null)} columns={columns} onSave={handleUpdateUser} />}
            {isAddUserModalOpen && <AddUserModal onClose={() => setIsAddUserModalOpen(false)} onSubmit={handleCreateUser} groups={groups} columns={columns} />}
            {toast.show && <ToastNotification message={toast.message} type={toast.type} onHide={() => setToast({ ...toast, show: false })} />}

        </div>

    );
}
